import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import { Loader2, Sparkles, Mail, KeyRound, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * Stripe-first onboarding — hit after successful Stripe Checkout.
 * URL: /checkout/complete?session_id=cs_test_...
 *
 * Flow:
 *  1) Poll /api/stripe/checkout-status until payment_status === "paid".
 *  2) Fetch /api/auth/checkout-context to obtain the email + know whether an
 *     account already exists for it.
 *  3a) NEW user → collect password → POST /auth/complete-signup → login.
 *  3b) EXISTING user → single click "Se connecter à mon compte" → same endpoint
 *      (server ignores the password field for existing users but a stub is
 *      required by the pydantic model, so we send a random one).
 */
export default function CheckoutComplete() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nextUrl = params.get("next") || "/account";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [phase, setPhase] = useState("verifying"); // verifying | context | ready | done
  const [ctx, setCtx] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // 1) Poll payment status
  useEffect(() => {
    if (!sessionId) { setError("Session introuvable"); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/stripe/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setPhase("context");
          return;
        }
        attempts += 1;
        if (attempts > 30) {
          setError("Le paiement met plus de temps que prévu. Rechargez la page dans quelques instants.");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setError(e?.response?.data?.detail || "Erreur de vérification du paiement");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  // 2) Fetch buyer context
  useEffect(() => {
    if (phase !== "context") return;
    api.get(`/auth/checkout-context/${sessionId}`)
      .then((r) => { setCtx(r.data); setPhase("ready"); })
      .catch((e) => setError(e?.response?.data?.detail || "Contexte de commande introuvable"));
  }, [phase, sessionId]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!ctx) return;
    if (!ctx.user_exists && password.length < 6) {
      toast.error("Choisissez un mot de passe (6 caractères minimum)");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        session_id: sessionId,
        password: ctx.user_exists ? Math.random().toString(36).slice(2, 12) : password,
        name: name || undefined,
      };
      const { data } = await api.post("/auth/complete-signup", payload);
      auth.setToken(data.access_token);
      auth.setUser(data.user);
      if (refresh) await refresh();
      setPhase("done");
      toast.success(ctx.user_exists ? "Achat rattaché à votre compte" : "Bienvenue chez geoVINUM !");
      const target = nextUrl === "/shop/order" ? `/shop/order?session_id=${sessionId}` : nextUrl;
      setTimeout(() => navigate(target, { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de finaliser l'inscription");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-5 py-16" data-testid="checkout-complete-error">
        <Toaster theme="light" />
        <div className="app-card p-6 text-center">
          <h1 className="font-display text-2xl text-[#1F3A5F]">Un souci est survenu</h1>
          <p className="text-sm text-[#4A5568] mt-2">{error}</p>
          <Link to="/pricing" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#E5276A]">
            Retour aux offres <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  if (phase === "verifying" || phase === "context") {
    return (
      <div className="max-w-lg mx-auto px-5 py-24 flex flex-col items-center gap-3 text-[#8B95A1]" data-testid="checkout-complete-verifying">
        <Loader2 className="w-6 h-6 animate-spin text-[#E5276A]" />
        <p className="text-sm">Vérification du paiement…</p>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="checkout-complete-done">
        <CheckCircle2 className="w-14 h-14 text-[#22C55E] mx-auto" />
        <h1 className="font-display text-3xl text-[#1F3A5F] mt-4">Tout est prêt !</h1>
        <p className="text-sm text-[#4A5568] mt-2">Redirection vers votre compte…</p>
      </div>
    );
  }

  // phase === "ready"
  return (
    <div className="max-w-lg mx-auto px-5 py-14 md:py-20" data-testid="checkout-complete-page">
      <Toaster theme="light" />
      <div className="app-card p-6 md:p-8">
        <div className="flex items-center gap-2 text-[#22C55E] text-sm font-bold mb-4">
          <CheckCircle2 className="w-5 h-5" /> Paiement confirmé
        </div>
        <h1 className="font-display text-3xl text-[#1F3A5F] leading-tight">
          {ctx.user_exists ? "Bon retour parmi nous" : "Créez votre accès geoVINUM"}
        </h1>
        <p className="text-sm text-[#4A5568] mt-2">
          {ctx.user_exists
            ? "Votre achat est prêt à être rattaché à votre compte existant."
            : "Encore une étape : définissez le mot de passe qui protégera votre carnet et vos préférences."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-[#8B95A1] inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email de paiement
            </span>
            <input
              type="email"
              value={ctx.email}
              readOnly
              disabled
              data-testid="checkout-complete-email"
              className="mt-1 w-full px-4 py-2.5 bg-[#FBF7F3] border border-[#1F3A5F]/12 rounded-xl text-sm text-[#1F3A5F] cursor-not-allowed"
            />
          </label>

          {!ctx.user_exists && (
            <>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-[#8B95A1] inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Votre prénom (optionnel)
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Comme vous voulez que geoVINUM AI vous appelle"
                  data-testid="checkout-complete-name"
                  className="mt-1 w-full px-4 py-2.5 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] rounded-xl text-sm text-[#1F3A5F] outline-none transition-colors"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-[#8B95A1] inline-flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5" /> Choisissez un mot de passe
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="checkout-complete-password"
                  className="mt-1 w-full px-4 py-2.5 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] rounded-xl text-sm text-[#1F3A5F] outline-none transition-colors"
                  placeholder="6 caractères minimum"
                />
              </label>
            </>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="checkout-complete-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" />
              : ctx.user_exists ? "Se connecter à mon compte" : "Créer mon accès"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="mt-6 text-[11px] text-[#8B95A1] leading-relaxed">
          En finalisant l&apos;inscription vous acceptez nos <Link to="/legal/cgu" className="underline">conditions générales</Link>.
          Votre paiement est sécurisé par Stripe.
        </p>
      </div>
    </div>
  );
}
